import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, DeviceMobile, Wrench, Plus, PencilSimple, X, Check, Printer, Trash, Package, CaretRight, LockSimple, LockSimpleOpen } from "@phosphor-icons/react";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";

const fmtDate = (d) => { const p = new Date(d); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };

export default function ClienteDettaglio() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editPhone2, setEditPhone2] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [assetInput, setAssetInput] = useState("");
  const [editDevice, setEditDevice] = useState(null);
  const [devBrand, setDevBrand] = useState("");
  const [devModel, setDevModel] = useState("");
  const [devSerial, setDevSerial] = useState("");
  const [devSaving, setDevSaving] = useState(false);
  const [expandedRepair, setExpandedRepair] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const load = () => api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => {});

  useEffect(() => { load(); }, [id]);

  const startEdit = () => {
    setEditName(client.name);
    setEditPhone(client.phone);
    setEditPhone2(client.phone2 || "");
    setEditEmail(client.email || "");
    setEditNotes(client.notes || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editName.trim() || !editPhone.trim()) {
      toast.error("Nome e telefono principale sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/clients/${id}`, {
        name: editName.trim(),
        phone: editPhone.trim(),
        phone2: editPhone2.trim(),
        email: editEmail.trim(),
        notes: editNotes,
      });
      toast.success("Scheda cliente aggiornata");
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAssets = async (next) => {
    try {
      await api.patch(`/clients/${id}`, { assets: next });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addAsset = async () => {
    const v = assetInput.trim();
    if (!v) return;
    await saveAssets([...(client.assets || []), v]);
    setAssetInput("");
  };

  const removeAsset = async (i) => {
    await saveAssets((client.assets || []).filter((_, idx) => idx !== i));
  };

  const startDeviceEdit = (d) => {
    setEditDevice(d);
    setDevBrand(d.brand || "");
    setDevModel(d.model || "");
    setDevSerial(d.serial || "");
  };

  const saveDeviceEdit = async () => {
    if (!devBrand.trim() || !devModel.trim()) {
      toast.error("Marca e modello sono obbligatori");
      return;
    }
    setDevSaving(true);
    try {
      await api.patch(`/devices/${editDevice.id}`, { brand: devBrand.trim(), model: devModel.trim(), serial: devSerial.trim() });
      toast.success("Dispositivo aggiornato");
      setEditDevice(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDevSaving(false);
    }
  };

  const removeDevice = async (deviceId) => {
    if (!window.confirm("Eliminare questo dispositivo dalla scheda del cliente? Le riparazioni già registrate restano nello storico.")) return;
    try {
      await api.delete(`/devices/${deviceId}`);
      toast.success("Dispositivo eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!client) {
    return (
      <div className="p-10 flex justify-center" data-testid="client-detail-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="cliente-dettaglio-page">
      <div>
        <Link to="/clienti" data-testid="back-to-clients-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-4 transition-colors duration-150">
          <ArrowLeft size={15} /> Tutti i clienti
        </Link>
        {editing ? (
          <div className="border border-border rounded-md bg-card p-6 space-y-4 max-w-3xl" data-testid="client-edit-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nome e cognome *</label>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-client-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefono principale *</label>
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} data-testid="edit-client-phone-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Secondo telefono (opzionale)</label>
                <input value={editPhone2} onChange={(e) => setEditPhone2(e.target.value)} data-testid="edit-client-phone2-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} data-testid="edit-client-email-input" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Note</label>
              <input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} data-testid="edit-client-notes-input" className={inputCls} />
            </div>
            <div className="flex gap-2">
              <button onClick={saveEdit} disabled={saving} data-testid="save-client-edit-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                <Check size={16} weight="bold" /> {saving ? "Salvataggio..." : "Salva modifiche"}
              </button>
              <button onClick={() => setEditing(false)} data-testid="cancel-client-edit-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <X size={16} weight="bold" /> Annulla
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none" data-testid="client-detail-name">{client.name}</h1>
              <p className="text-sm text-muted-foreground mt-2" data-testid="client-detail-phone">
                {client.phone}
                {client.phone2 ? ` · ${client.phone2}` : ""}
                {client.email ? ` · ${client.email}` : ""}
              </p>
              {client.notes && <p className="text-sm text-muted-foreground mt-1" data-testid="client-detail-notes">{client.notes}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={startEdit} data-testid="edit-client-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
                <PencilSimple size={16} /> Modifica scheda
              </button>
              <Link to={`/riparazioni?cliente=${client.id}`} data-testid="new-repair-for-client-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
                <Plus size={16} /> Nuova riparazione
              </Link>
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
          <DeviceMobile size={20} weight="duotone" /> Dispositivi ({client.devices.length})
        </h2>
        {client.devices.length === 0 ? (
          <div className="border border-border rounded-md bg-card p-8 text-center text-muted-foreground text-sm" data-testid="devices-empty">
            Nessun dispositivo registrato
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="devices-grid">
            {client.devices.map((d) => (
              <div key={d.id} className="border border-border rounded-md bg-card p-5" data-testid={`device-card-${d.id}`}>
                {editDevice?.id === d.id ? (
                  <div className="space-y-3" data-testid={`device-edit-form-${d.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Marca *</label>
                        <input value={devBrand} onChange={(e) => setDevBrand(e.target.value)} data-testid="device-edit-brand-input" className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Modello *</label>
                        <input value={devModel} onChange={(e) => setDevModel(e.target.value)} data-testid="device-edit-model-input" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Seriale (opzionale)</label>
                      <input value={devSerial} onChange={(e) => setDevSerial(e.target.value)} data-testid="device-edit-serial-input" className={inputCls} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveDeviceEdit} disabled={devSaving} data-testid="device-edit-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                        <Check size={14} weight="bold" /> {devSaving ? "Salvataggio..." : "Salva"}
                      </button>
                      <button onClick={() => setEditDevice(null)} data-testid="device-edit-cancel"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-border text-xs font-bold text-muted-foreground hover:text-foreground transition-colors duration-150">
                        <X size={14} weight="bold" /> Annulla
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-heading font-bold">{d.brand} {d.model}</p>
                      <p className="text-xs text-muted-foreground capitalize mt-1">{d.type}{d.serial ? ` · SN: ${d.serial}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 mt-0.5">
                      <button onClick={() => startDeviceEdit(d)} title="Modifica dispositivo (marca, modello, seriale)"
                        data-testid={`device-edit-${d.id}`}
                        className="text-muted-foreground hover:text-primary transition-colors duration-150">
                        <PencilSimple size={15} />
                      </button>
                      <button onClick={() => removeDevice(d.id)} title="Elimina dispositivo (il cliente non lo possiede più)"
                        data-testid={`device-delete-${d.id}`}
                        className="text-muted-foreground hover:text-destructive transition-colors duration-150">
                        <Trash size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
          <Printer size={20} weight="duotone" /> Apparati annotati ({(client.assets || []).length})
        </h2>
        <div className="border border-border rounded-md bg-card p-5 space-y-4" data-testid="assets-section">
          {(client.assets || []).length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="assets-empty">
              Nessun apparato annotato. Esempio: "Stampante HP LaserJet Pro — toner CE285A".
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="assets-list">
              {(client.assets || []).map((a, i) => (
                <li key={i} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3" data-testid={`asset-item-${i}`}>
                  <span className="text-sm">{a}</span>
                  <button onClick={() => removeAsset(i)} data-testid={`asset-remove-${i}`}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-150 shrink-0">
                    <Trash size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <input value={assetInput} onChange={(e) => setAssetInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAsset(); } }}
              placeholder="Es. Stampante HP LaserJet Pro — toner CE285A"
              data-testid="asset-input" className={inputCls} />
            <button onClick={addAsset} data-testid="asset-add-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150 shrink-0">
              <Plus size={15} /> Aggiungi
            </button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
          <Wrench size={20} weight="duotone" /> Storico riparazioni ({client.repairs.length})
        </h2>
        {client.repairs.length > 0 && (
          <div className="border border-primary/40 bg-primary/5 rounded-md px-5 py-4 mb-4 flex items-center gap-6 flex-wrap" data-testid="repairs-summary">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Riparazioni concluse</p>
              <p className="font-heading text-xl font-extrabold" data-testid="repairs-concluded-count">{client.repairs_concluded ?? 0}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Totale incassato da questo cliente</p>
              <p className="font-heading text-xl font-extrabold text-green-500" data-testid="repairs-revenue-total">
                € {Number(client.repairs_revenue || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        )}
        {client.repairs.length === 0 ? (
          <div className="border border-border rounded-md bg-card p-8 text-center text-muted-foreground text-sm" data-testid="history-empty">
            Nessuna riparazione effettuata
          </div>
        ) : (
          <div className="border border-border rounded-md bg-card divide-y divide-border" data-testid="repairs-history">
            {client.repairs.map((r) => { const open = expandedRepair === r.id; return (
              <div key={r.id} className="px-5 py-4 cursor-pointer hover:bg-accent/40 transition-colors duration-150"
                onClick={() => setExpandedRepair(open ? null : r.id)}
                data-testid={`history-repair-${r.id}`}>
                <div className="flex items-start gap-2.5">
                  <CaretRight size={15} className={`mt-1 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                      <p className="text-sm font-bold">{r.device_label}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        r.status === "in_lavorazione" ? "bg-amber-500/15 text-amber-500" : r.picked_up ? "bg-green-500/15 text-green-500" : "bg-blue-500/15 text-blue-500"}`}>
                        {r.status === "in_lavorazione" ? "In lavorazione" : r.picked_up ? "Ritirata" : "Completato"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{r.problem}</p>
                    {r.resolution && (
                      <p className="text-sm mt-1" data-testid={`history-resolution-${r.id}`}>
                        <span className="text-muted-foreground">Intervento eseguito: </span>{r.resolution}
                      </p>
                    )}
                    {r.internal_notes && (
                      <p className="text-xs mt-1 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-500" data-testid={`history-internal-${r.id}`}>
                        <b>Uso interno:</b> {r.internal_notes}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {fmtDate(r.created_at)} · Tecnico: {r.technician_name}
                      {r.price != null && ` · € ${Number(r.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`}
                    </p>
                  </div>
                </div>
                {open && (
                  <div className="mt-3 ml-6 pt-3 border-t border-border grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-muted-foreground" data-testid={`history-detail-${r.id}`}>
                    <p className="flex items-center gap-1.5" data-testid={`history-password-${r.id}`}>
                      {r.no_password
                        ? <><LockSimpleOpen size={13} className="shrink-0" /> Senza password</>
                        : <><LockSimple size={13} className="shrink-0" /> Codice sblocco: <b className="text-foreground">{r.unlock_password}</b></>}
                    </p>
                    {(r.device_serial || r.serial) && <p>Seriale: <span className="text-foreground">{r.device_serial || r.serial}</span></p>}
                    {r.subcategory && <p>Sottocategoria: <span className="text-foreground">{r.subcategory}</span></p>}
                    {r.estimated_delivery && <p>Consegna stimata: <span className="text-foreground">{r.estimated_delivery}</span></p>}
                    {r.created_by && <p>Registrata da: <span className="text-foreground">{r.created_by}</span></p>}
                    {r.completed_at && <p>Completata il: <span className="text-foreground">{fmtDate(r.completed_at)}</span></p>}
                    {r.picked_up && <p>Ritirata dal cliente: <span className="text-foreground">{r.picked_up_at ? fmtDate(r.picked_up_at) : "sì"}</span></p>}
                    {r.whatsapp_notified_at && <p>Avviso WhatsApp inviato: <span className="text-foreground">{fmtDate(r.whatsapp_notified_at)}</span></p>}
                    <p>Pagamento: <span className={`font-bold ${r.cash_registered ? "text-green-500" : "text-amber-500"}`} data-testid={`history-payment-${r.id}`}>
                      {r.cash_registered ? (r.payment_pending ? "Da incassare (in Prima Nota)" : "Registrato in Prima Nota") : "Non registrato in Prima Nota"}
                    </span></p>
                    {r.order_id && <p>Origine: <span className="text-violet-400 font-semibold">Da ordine ricambio</span></p>}
                    {r.notes && <p className="sm:col-span-2">Note: <span className="text-foreground">{r.notes}</span></p>}
                    {(r.accessories || []).length > 0 && (
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-1.5" data-testid={`history-accessories-${r.id}`}>
                        <span>Consegnato col dispositivo:</span>
                        {r.accessories.map((a) => (
                          <span key={a} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-[10px] font-semibold">{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
      </div>
      <div>
        <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
          <Package size={20} weight="duotone" /> Ordini ricambi ({(client.orders || []).length})
        </h2>
        {(client.orders || []).length > 0 && (
          <div className="border border-primary/40 bg-primary/5 rounded-md px-5 py-4 mb-4 flex items-center gap-6 flex-wrap" data-testid="orders-summary">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Ordini ritirati</p>
              <p className="font-heading text-xl font-extrabold" data-testid="orders-delivered-count">{client.orders_delivered ?? 0}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Totale ordini ritirati</p>
              <p className="font-heading text-xl font-extrabold text-green-500" data-testid="orders-revenue-total">
                € {Number(client.orders_revenue || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        )}
        {(client.orders || []).length === 0 ? (
          <div className="border border-border rounded-md bg-card p-8 text-center text-muted-foreground text-sm" data-testid="orders-empty">
            Nessun ordine ricambi effettuato
          </div>
        ) : (
          <div className="border border-border rounded-md bg-card divide-y divide-border" data-testid="orders-history">
            {(client.orders || []).map((o) => { const open = expandedOrder === o.id; return (
              <div key={o.id} className="px-5 py-4 cursor-pointer hover:bg-accent/40 transition-colors duration-150"
                onClick={() => setExpandedOrder(open ? null : o.id)}
                data-testid={`history-order-${o.id}`}>
                <div className="flex items-start gap-2.5">
                  <CaretRight size={15} className={`mt-1 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <p className="text-sm font-bold">{o.article}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        o.status === "ritirato" ? "bg-green-500/15 text-green-500"
                        : o.status === "arrivato" ? "bg-blue-500/15 text-blue-500"
                        : o.status === "in_riparazione" ? "bg-violet-500/15 text-violet-500"
                        : o.status === "ordinato" ? "bg-amber-500/15 text-amber-500"
                        : "bg-red-500/15 text-red-500"}`} data-testid={`history-order-status-${o.id}`}>
                        {o.status === "da_ordinare" ? "Da ordinare" : o.status === "ordinato" ? "Ordinato"
                          : o.status === "arrivato" ? "Arrivato" : o.status === "in_riparazione" ? "In riparazione" : "Ritirato"}
                      </span>
                    </div>
                    {o.notes && <p className="text-sm text-muted-foreground mt-1">{o.notes}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {fmtDate(o.created_at)}
                      {o.price != null && ` · € ${Number(o.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`}
                      {o.deposit != null && o.deposit > 0 && ` · acconto € ${Number(o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`}
                    </p>
                  </div>
                </div>
                {open && (
                  <div className="mt-3 ml-6 pt-3 border-t border-border grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-muted-foreground" data-testid={`history-order-detail-${o.id}`}>
                    {o.assignee_name && <p>Assegnato a: <span className="text-foreground">{o.assignee_name}</span></p>}
                    {o.created_by && <p>Registrato da: <span className="text-foreground">{o.created_by}</span></p>}
                    <p>Inserito il: <span className="text-foreground">{fmtDate(o.created_at)}</span></p>
                    {o.ordered_at && <p>Ordinato il: <span className="text-foreground">{fmtDate(o.ordered_at)}</span></p>}
                    {o.arrived_at && <p>Arrivato il: <span className="text-foreground">{fmtDate(o.arrived_at)}</span></p>}
                    {o.status === "ritirato" && <p>Ritirato dal cliente: <span className="text-foreground">sì</span></p>}
                    <p>Prezzo concordato: <span className="text-foreground">{o.price != null && o.price > 0 ? `€ ${Number(o.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}</span></p>
                    {(o.deposit != null && o.deposit > 0) && (
                      <p>Acconto: <span className="text-foreground">€ {Number(o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                        <span className={`ml-1.5 font-bold ${o.deposit_registered ? "text-green-500" : "text-amber-500"}`} data-testid={`history-order-deposit-${o.id}`}>
                          {o.deposit_registered ? "(in Prima Nota)" : "(non registrato)"}
                        </span>
                      </p>
                    )}
                    <p>Saldo: <span className={`font-bold ${o.payment_registered ? "text-green-500" : "text-amber-500"}`} data-testid={`history-order-payment-${o.id}`}>
                      {o.payment_registered ? "Registrato in Prima Nota" : o.status === "ritirato" ? "Non registrato in Prima Nota" : "Da saldare al ritiro"}
                    </span></p>
                    {o.whatsapp_notified_at && <p>Avviso WhatsApp inviato: <span className="text-foreground">{fmtDate(o.whatsapp_notified_at)}</span></p>}
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
}
